import { useMutation, useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { CompanyEventEnumType, CompanyEventStatusEnumType } from '__generated__/graphql';
import { FC } from 'react';

import Alert from 'primitives/Alert';
import Badge from 'primitives/Badge';
import Button from 'primitives/Button';
import ConfirmButton from 'primitives/ConfirmButton';
import LoadingIndicator from 'primitives/LoadingIndicator';
import SlideOver from 'primitives/SlideOver';
import Toast from 'primitives/Toast';
import useToast from 'primitives/Toast/useToast';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';
import { FormInput, FormPanel, FormPanelWithReadMode } from 'components/FormPanel';

import companyEventStatusToReadable from 'utils/enums/company-event-status-to-readable';

import { InvestorRightsOptions } from '../constants';

const COMPANY_EVENT_QUERY = gql(`#graphql
  query getCompanyEvent($id: ID!) {
    companyEvent(id: $id) {
      id
      label
      type
      eventDate
      isAuthorizationRequired
      authorizationStatus
      description
      notes
      investorRightsAdded
      investorRightsRemoved
      createdAt
      documents {
        id
        url
        name
        type
      }
      eventWebhook {
        senderId
      }
      shareAssets {
        shareAssetId
        status
        isAuthorizationRequired
        shareAsset {
          id
          shareClass {
            name
          }
          deal {
            schemeName
          }
        }
      }
      convertibleAssets {
        convertibleAssetId
        status
        isAuthorizationRequired
        convertibleAsset {
          id
          convertibleClass {
            name
          }
          deal {
            schemeName
          }
        }
      }
      deals {
        dealId
        status
        isAuthorizationRequired
        deal {
          schemeName
        }
      }
    }
  }
`);

const UPDATE_COMPANY_EVENT_MUTATION = gql(`
  mutation UpdateCompanyEvent(
    $id: ID!,
    $label: String!,
    $type: CompanyEventEnumType!,
    $eventDate: String!,
    $isAuthorizationRequired: Boolean!,
    $description: String,
    $notes: String,
    $investorRightsAdded: [InvestorRightEnumType!],
    $investorRightsRemoved: [InvestorRightEnumType!]
  ) {
    updateCompanyEvent(
      id: $id,
      label: $label,
      type: $type,
      eventDate: $eventDate,
      isAuthorizationRequired: $isAuthorizationRequired,
      description: $description,
      notes: $notes,
      investorRightsAdded: $investorRightsAdded,
      investorRightsRemoved: $investorRightsRemoved
    ) {
      id
      label
      type
      eventDate
      isAuthorizationRequired
      authorizationStatus
      description
      notes
      investorRightsAdded
      investorRightsRemoved
    }
  }
`);

const APPROVE_EVENT_AS_ADMIN = gql(`
  mutation approveEventAsAdmin($id: ID!, $status: CompanyEventStatusEnumType!, $documents: [Upload], $message: String, $replyToEmailId: String) {
    approveEventAsAdmin(id: $id, status: $status, documents: $documents, message: $message, replyToEmailId: $replyToEmailId) {
      id
      authorizationStatus
      documents {
        id
        url
        name
        type
      }
    }
  }
`);

const SEND_EVENT_REMINDER_MUTATION = gql(`
  mutation SendCompanyEventReminder($id: ID!, $assetId: ID!) {
    sendEventReminderToLead(id: $id, assetId: $assetId)
  }
`);

const ADD_DOCUMENTS_TO_COMPANY_EVENT_MUTATION = gql(`
  mutation AddDocumentsToCompanyEvent($id: ID!, $documents: [Upload!]!, $type: CompanyEventEnumType!) {
    addDocumentsToCompanyEvent(id: $id, documents: $documents, type: $type) {
      id
      documents {
        id
        url
        name
        type
      }
    }
  }
`);

const REMOVE_COMPANY_EVENT_MUTATION = gql(`
  mutation RemoveCompanyEvent($id: ID!) {
    removeCompanyEvent(id: $id) {
      id
    }
  }
`);

const REMOVE_DOCUMENT_FROM_COMPANY_EVENT_MUTATION = gql(`
  mutation RemoveDocumentFromCompanyEvent($id: ID!) {
    removeCompanyEventDocument(id: $id) {
      id
    }
  }
`);

const CompanyEventSlideOver: FC<{
  open: boolean;
  companyEventId: string;
  onClose: () => void;
}> = ({ open = false, companyEventId, onClose }) => {
  const { loading, error, data, refetch } = useQuery(COMPANY_EVENT_QUERY, {
    variables: {
      id: companyEventId,
    },
    skip: !open,
  });

  const { showToast, handleToastOpen, handleToastClose } = useToast();
  const {
    showToast: showEventApprovalToast,
    handleToastOpen: handleEventApprovalToastOpen,
    handleToastClose: handleEventApprovalToastClose,
  } = useToast();

  const [updateCompanyEvent] = useMutation(UPDATE_COMPANY_EVENT_MUTATION, {
    update(cache, { data }) {
      if (!data) return;
      cache.modify({
        fields: {
          events(existingCompanyEvents) {
            const newCompanyEventRef = cache.writeFragment({
              data: data.updateCompanyEvent,
              fragment: gql(`
                fragment NewCompanyEventSlideOver on CompanyEventType {
                  id
                  label
                  type
                  eventDate
                  isAuthorizationRequired
                  authorizationStatus
                  description
                  notes
                  investorRightsAdded
                  investorRightsRemoved
                }
              `),
            });
            const updatedCompanyEvents = { ...existingCompanyEvents };
            const existingIndex = updatedCompanyEvents.nodes.findIndex(
              (node: any) => node.__ref === `CompanyEventType:${data.updateCompanyEvent?.id}`
            );
            if (existingIndex > -1) {
              updatedCompanyEvents.nodes[existingIndex] = newCompanyEventRef;
            }
            return updatedCompanyEvents;
          },
        },
      });
    },
    refetchQueries: ['fetchEventsByCompany'],
  });

  const [addDocuments, { loading: addDocumentsLoading, error: addDocumentsError }] = useMutation(
    ADD_DOCUMENTS_TO_COMPANY_EVENT_MUTATION,
    {
      update(cache, { data }) {
        if (!data) return;
        cache.modify({
          fields: {
            companyEvent(existingCompanyEvent) {
              const newCompanyEventRef = cache.writeFragment({
                data: data.addDocumentsToCompanyEvent,
                fragment: gql(`
                fragment NewCompanyEventDocuments on CompanyEventType {
                  id
                  documents {
                    id
                    url
                    name
                    type
                  }
                }
              `),
              });
              return newCompanyEventRef;
            },
          },
        });
      },
      refetchQueries: ['getCompanyEvent'],
    }
  );

  const [approveEventAsAdmin, { loading: approvalLoading }] = useMutation(APPROVE_EVENT_AS_ADMIN, {
    update(cache, { data }) {
      if (!data) return;
      cache.modify({
        fields: {
          events(existingCompanyEvents) {
            const newCompanyEventRef = cache.writeFragment({
              data: data.approveEventAsAdmin,
              fragment: gql(`
                fragment NewCompanyEventApproval on CompanyEventType {
                  id
                  authorizationStatus
                  documents {
                    id
                    url
                    name
                    type
                  }
                }
              `),
            });
            const updatedCompanyEvents = { ...existingCompanyEvents };
            const existingIndex = updatedCompanyEvents.nodes.findIndex(
              (node: any) => node.__ref === `CompanyEventType:${data.approveEventAsAdmin?.id}`
            );
            if (existingIndex > -1) {
              updatedCompanyEvents.nodes[existingIndex] = newCompanyEventRef;
            }
            return updatedCompanyEvents;
          },
        },
      });
    },
    refetchQueries: ['getCompanyEvent'],
  });

  const [sendReminder, { loading: reminderLoading }] = useMutation(SEND_EVENT_REMINDER_MUTATION);

  const [removeCompanyEvent, { loading: removeCompanyEventLoading }] = useMutation(
    REMOVE_COMPANY_EVENT_MUTATION,
    {
      refetchQueries: ['getCompanyEvent'],
    }
  );

  const [removeDocumentFromCompanyEvent, { loading: removeDocumentFromCompanyEventLoading }] =
    useMutation(REMOVE_DOCUMENT_FROM_COMPANY_EVENT_MUTATION, {
      refetchQueries: ['getCompanyEvent'],
    });

  const handleSendReminder = async (assetId: string) => {
    try {
      await sendReminder({
        variables: {
          id: companyEventId,
          assetId,
        },
      }).then(() => {
        handleToastOpen();
      });
    } catch (error) {
      // Do nothing
    }
  };

  const renderContent = () => {
    if (loading) return <LoadingIndicator />;

    if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

    const event = data.companyEvent;

    return (
      <div>
        <FormPanelWithReadMode
          loading={loading}
          error={error}
          onSubmit={async data => {
            await updateCompanyEvent({
              variables: {
                id: event.id,
                type: event.type,
                isAuthorizationRequired: data.isAuthorizationRequired,
                description: data.description,
                eventDate: new Date(data.eventDate).toISOString(),
                label: data.label,
                notes: data.notes,
                investorRightsAdded: data.investorRightsAdded,
                investorRightsRemoved: data.investorRightsRemoved,
              },
            });
          }}
        >
          <FormInput
            type="select"
            fieldName="type"
            label="Type"
            defaultValue={event.type}
            options={[
              {
                value: CompanyEventEnumType.TransactionalDocuments,
                label: 'Transactional Documents',
              },
              {
                value: CompanyEventEnumType.AncillaryDocuments,
                label: 'Ancillary Documents',
              },
              {
                value: CompanyEventEnumType.RegulatoryDocuments,
                label: 'Regulatory Documents',
              },
              { value: CompanyEventEnumType.Others, label: 'Others' },
            ]}
          />
          <FormInput type="text" fieldName="label" label="Label" defaultValue={event.label} />
          <FormInput
            type="date"
            fieldName="eventDate"
            label="Event Date"
            defaultValue={event.eventDate}
          />
          <FormInput
            type="text"
            fieldName="description"
            label="Description"
            defaultValue={event.description || ''}
          />
          <FormInput
            type="switch"
            fieldName="isAuthorizationRequired"
            label="Authorization Required"
            defaultValue={event.isAuthorizationRequired || false}
          />
          <FormInput
            type="textarea"
            fieldName="notes"
            label="Notes"
            defaultValue={event.notes || ''}
          />
          <FormInput
            type="multi_select"
            compact
            fieldName="investorRightsAdded"
            label="Investor Rights Added"
            defaultValue={event.investorRightsAdded || []}
            options={InvestorRightsOptions}
          />
          <FormInput
            type="multi_select"
            compact
            fieldName="investorRightsRemoved"
            label="Investor Rights Removed"
            defaultValue={event.investorRightsRemoved || []}
            options={InvestorRightsOptions}
          />
          <div className="col-span-full">
            <DataTable
              data={event.documents || []}
              onClick={() => {}}
              columns={[
                {
                  label: 'Name',
                  fieldName: 'name',
                },
                {
                  label: 'Type',
                  fieldName: 'type',
                  type: 'COMPANY_EVENT_TYPE',
                },
                {
                  label: 'Link',
                  fieldName: 'url',
                  type: 'CUSTOM',
                  customValue: url => {
                    return (
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-600 hover:text-indigo-800"
                      >
                        View
                      </a>
                    );
                  },
                },
                {
                  label: 'Actions',
                  fieldName: 'id',
                  type: 'CUSTOM',
                  customValue: id => {
                    return (
                      <ConfirmButton
                        variant="secondary"
                        className="text-red-900 hover:text-red-700"
                        onConfirm={() => {
                          removeDocumentFromCompanyEvent({
                            variables: {
                              id,
                            },
                          });
                        }}
                        title="Delete Webhook"
                        loading={removeDocumentFromCompanyEventLoading}
                        description="Are you sure you want to delete this document?"
                      >
                        Delete
                      </ConfirmButton>
                    );
                  },
                },
              ]}
              hasNextPage={false}
              paginationLoading={loading}
            />
          </div>
          <div className="flex flex-col gap-y-2 w-full col-span-full mt-8">
            <h3 className="font-semibold mb-2 text-base">Assets impacted</h3>
            {event.shareAssets?.map((asset, index) => (
              <div key={index} className="mb-2">
                <p className="mb-1 text-sm">
                  <span>{asset!.shareAsset.deal.schemeName}</span>
                  <span> ({asset!.shareAsset.shareClass.name})</span>
                  <span className="ml-3">
                    <Badge {...companyEventStatusToReadable(asset!.status)} size="sm" />
                  </span>
                </p>
                <Button
                  variant="secondary"
                  onClick={() => handleSendReminder(asset!.shareAssetId)}
                  disabled={reminderLoading}
                >
                  {reminderLoading ? 'Sending...' : 'Send Reminder'}
                </Button>
              </div>
            ))}
            {event.convertibleAssets?.map((asset, index) => (
              <div key={index} className="mb-2">
                <p className="mb-1 text-sm">
                  <span>{asset!.convertibleAsset.deal.schemeName}</span>
                  <span> ({asset!.convertibleAsset.convertibleClass.name})</span>
                  <span className="ml-3">
                    <Badge {...companyEventStatusToReadable(asset!.status)} size="sm" />
                  </span>
                </p>
                <Button
                  variant="secondary"
                  onClick={() => handleSendReminder(asset!.convertibleAssetId)}
                  disabled={reminderLoading}
                >
                  {reminderLoading ? 'Sending...' : 'Send Reminder'}
                </Button>
              </div>
            ))}
            {event.deals?.map((deal, index) => (
              <div key={index} className="mb-2">
                <p className="mb-1 text-sm">
                  <span>{deal!.deal.schemeName}</span>
                  <span className="ml-3">
                    <Badge {...companyEventStatusToReadable(deal!.status)} size="sm" />
                  </span>
                </p>
                <Button
                  variant="secondary"
                  onClick={() => handleSendReminder(deal!.dealId)}
                  disabled={reminderLoading}
                >
                  {reminderLoading ? 'Sending...' : 'Send Reminder'}
                </Button>
              </div>
            ))}
          </div>
        </FormPanelWithReadMode>

        <div className="mt-8 p-4 border rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Documents</h3>
          <FormPanel
            loading={addDocumentsLoading}
            error={addDocumentsError}
            onCancel={() => {}}
            onSubmit={async formData => {
              if (!formData.newDocuments || formData.newDocuments.length === 0) {
                return;
              }
              await addDocuments({
                variables: {
                  id: event.id,
                  documents: formData.newDocuments,
                  type: formData.documentType,
                },
              });
            }}
          >
            <FormInput
              type="select"
              fieldName="documentType"
              label="Document Type"
              validators={{ required: true }}
              defaultValue={CompanyEventEnumType.ApprovalDocuments}
              options={[
                {
                  value: CompanyEventEnumType.TransactionalDocuments,
                  label: 'Transactional Documents',
                },
                {
                  value: CompanyEventEnumType.AncillaryDocuments,
                  label: 'Ancillary Documents',
                },
                {
                  value: CompanyEventEnumType.RegulatoryDocuments,
                  label: 'Regulatory Documents',
                },
                { value: CompanyEventEnumType.Others, label: 'Others' },
                { value: CompanyEventEnumType.ApprovalDocuments, label: 'Approval Documents' },
              ]}
            />
            <FormInput
              type="upload"
              fieldName="newDocuments"
              label="Select Files"
              defaultValue={[]}
              validators={{ required: true }}
            />
          </FormPanel>
        </div>

        <div className="mt-16 border border-px p-4 rounded-lg">
          <h3 className="font-semibold mb-8 text-base">Reply To Company</h3>
          {event.authorizationStatus !== CompanyEventStatusEnumType.Pending &&
            event.authorizationStatus !== CompanyEventStatusEnumType.NotApplicable && (
              <Alert
                type="info"
                className="mb-4"
                title={`This event has been ${
                  companyEventStatusToReadable(event.authorizationStatus).label
                }`}
                message="An email has already been sent to the company"
              />
            )}
          <FormPanel
            onSubmit={async approvalData => {
              await approveEventAsAdmin({
                variables: {
                  id: event.id,
                  status:
                    approvalData.authorizationStatus === true
                      ? CompanyEventStatusEnumType.Authorized
                      : CompanyEventStatusEnumType.Rejected,
                  documents: approvalData.approvalDocuments ? approvalData.approvalDocuments : [],
                  message: approvalData.customMessage,
                  replyToEmailId: approvalData.replyToEmailId,
                },
              });
              handleEventApprovalToastOpen();
            }}
            loading={approvalLoading}
            error={error}
          >
            <FormInput
              type="text"
              fieldName="replyToEmailId"
              label="Send To Email"
              defaultValue={event.eventWebhook?.senderId || ''}
            />
            <FormInput
              type="switch"
              fieldName="authorizationStatus"
              label="Authorization Status"
              defaultValue={true}
            />
            <FormInput
              type="upload"
              fieldName="approvalDocuments"
              label="Approval Documents"
              defaultValue={[]}
            />
            <FormInput
              type="textarea"
              fieldName="customMessage"
              label="Add a message (optional)"
              defaultValue={''}
            />
          </FormPanel>
        </div>

        <div className="mt-8">
          <ConfirmButton
            variant="secondary"
            onConfirm={() =>
              removeCompanyEvent({
                variables: { id: event.id },
              }).then(() => onClose())
            }
            loading={removeCompanyEventLoading}
            title="Remove Event"
            description="Are you sure you want to permanently remove this event?"
          >
            Remove Event
          </ConfirmButton>
        </div>
      </div>
    );
  };

  return (
    <SlideOver
      open={open}
      title="Company Events"
      subTitle="Manage Company Events"
      onClose={onClose}
    >
      {renderContent()}
      <Toast
        show={showToast}
        handleClose={handleToastClose}
        title="Reminder Sent"
        message="Reminder sent successfully"
      />
      <Toast
        show={showEventApprovalToast}
        handleClose={handleEventApprovalToastClose}
        title="Event Approved"
        message="Event approved successfully"
      />
    </SlideOver>
  );
};

export default CompanyEventSlideOver;
